/**
 * RetailIntegrityPane — v2.12.12
 *
 * Automated retail integrity dashboard. Runs 8 pre-cutover checks
 * (loyalty balance ≡ Σ txns, stock reconciliation, barcode integrity,
 * marketing consent) and freezes a hash-chained backup snapshot before
 * any production release.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, PlayCircle, RefreshCw, CheckCircle2, AlertTriangle,
  XCircle, Loader2, Archive, Info, Hash,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICT_META = {
  pass: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2, label: 'PASS' },
  warn: { color: 'bg-amber-100 text-amber-800 border-amber-200',   Icon: AlertTriangle, label: 'WARN' },
  fail: { color: 'bg-rose-100 text-rose-800 border-rose-200',      Icon: XCircle,       label: 'FAIL' },
  info: { color: 'bg-slate-100 text-slate-700 border-slate-200',   Icon: Info,          label: 'INFO' },
};

function VerdictPill({ status }) {
  const meta = VERDICT_META[status] || VERDICT_META.info;
  const Ic = meta.Icon;
  return (
    <Badge className={`${meta.color} text-[10px] border font-medium inline-flex items-center gap-1`}>
      <Ic className="w-3 h-3" /> {meta.label}
    </Badge>
  );
}

export default function RetailIntegrityPane({ activeProject }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backups, setBackups] = useState([]);
  const [snapping, setSnapping] = useState(false);

  const loadLatest = async () => {
    if (!activeProject) return;
    try {
      const { data } = await axios.get(`${API}/api/migration/retail/projects/${activeProject}/integrity/latest`);
      setRun(data);
    } catch { setRun(null); }
    try {
      const { data } = await axios.get(`${API}/api/migration/retail/projects/${activeProject}/backup/list`);
      setBackups(data.backups || []);
    } catch { setBackups([]); }
  };

  useEffect(() => { loadLatest(); /* eslint-disable-next-line */ }, [activeProject]);

  const doRun = async () => {
    if (!activeProject) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/retail/projects/${activeProject}/integrity/run`, { source_stock_map: {} });
      setRun(data);
      toast.success(`Integrity check ${data.verdict.toUpperCase()} · ${data.rules_run} rules run`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Integrity check failed'); }
    finally { setBusy(false); }
  };

  const doSnapshot = async () => {
    if (!activeProject) return;
    setSnapping(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/retail/projects/${activeProject}/backup/snapshot`);
      toast.success(`Backup frozen · hash ${data.hash.slice(0, 12)}…`);
      loadLatest();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Snapshot failed'); }
    finally { setSnapping(false); }
  };

  if (!activeProject) {
    return (
      <Card className="border-slate-200" data-testid="retail-integrity-pane-empty">
        <CardContent className="p-6 text-center text-sm text-slate-500">
          Select a Migration Project to run Retail Integrity checks.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="retail-integrity-pane">
      <Card className="border-emerald-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <CardTitle className="text-base sm:text-lg">Retail Integrity — Automated Pre-Cutover Checks</CardTitle>
            <Badge className="bg-slate-900 text-white text-[10px]">v2.12.12 · NEW</Badge>
          </div>
          <p className="text-xs text-slate-600 mt-1">
            8 automated rules across loyalty balance, stock reconciliation, barcode integrity, and marketing consent.
            Every release also freezes a hash-chained backup snapshot for triple-safety-net rollback.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={doRun} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="run-integrity-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Running…</> : <><PlayCircle className="w-4 h-4 mr-1.5" /> Run Integrity Check</>}
            </Button>
            <Button onClick={doSnapshot} disabled={snapping} variant="outline" data-testid="run-snapshot-btn">
              {snapping ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Freezing…</> : <><Archive className="w-4 h-4 mr-1.5" /> Freeze Backup Snapshot</>}
            </Button>
            <Button onClick={loadLatest} variant="ghost" size="sm"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          </div>
        </CardContent>
      </Card>

      {run && (
        <Card className="border-slate-200" data-testid="integrity-results-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Latest Verdict</CardTitle>
              <VerdictPill status={run.verdict} />
              <span className="text-[11px] text-slate-500">· {run.rules_run} rules run · {run.created_at?.slice(0, 19)?.replace('T', ' ')} UTC</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(run.findings || []).map((f, i) => (
                <div key={i} className="p-2.5 rounded-md bg-slate-50 border border-slate-200 flex items-start gap-2">
                  <VerdictPill status={f.status === 'pass' ? 'pass' : (f.severity === 'high' ? 'fail' : 'warn')} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <code className="text-[10px] px-1.5 py-0.5 bg-white rounded border border-slate-200">{f.rule_id}</code>
                      <span className="text-xs font-semibold text-slate-900">{f.title}</span>
                    </div>
                    <div className="text-[11px] text-slate-600 mt-1">{f.description}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Affected: {f.affected_count?.toLocaleString()} / {f.total_count?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {backups.length > 0 && (
        <Card className="border-slate-200" data-testid="backups-list-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Archive className="w-4 h-4 text-slate-600" /> Backup Snapshots ({backups.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1.5 border border-slate-200">Created</th>
                    <th className="text-right px-2 py-1.5 border border-slate-200">Sources</th>
                    <th className="text-right px-2 py-1.5 border border-slate-200">Mappings</th>
                    <th className="text-left px-2 py-1.5 border border-slate-200">Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b._id} className="hover:bg-slate-50">
                      <td className="px-2 py-1.5 border border-slate-200">{b.created_at?.slice(0, 19)?.replace('T', ' ')} UTC</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right">{b.summary?.sources_count || 0}</td>
                      <td className="px-2 py-1.5 border border-slate-200 text-right">{b.summary?.mappings_count || 0}</td>
                      <td className="px-2 py-1.5 border border-slate-200"><code className="text-[10px] flex items-center gap-1"><Hash className="w-3 h-3" /> {b.hash?.slice(0, 16)}…</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
